import { buildApiUrl, API_CONFIG } from '../config';
import { apiFetch } from '../utils/auth';

// Interface für Benutzerdaten
export interface User {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email?: string;
  orgID: number;
  active: boolean;
  hashKey: string; // Hash für NFC-Identifikation (von API /v1/users/active)
}

// Interface für NFC-Scan-Ergebnis
export interface NFCScanResult {
  success: boolean;
  user?: User;
  error?: string;
}

// Temporäre Test-Daten für NFC-Funktionalität (bis Server-Fehler behoben ist)
export const getTestUsers = (orgID: number): User[] => {
  return [
    {
      id: 1,
      username: 'testuser1',
      firstName: 'Max',
      lastName: 'Mustermann',
      email: 'max.mustermann@example.com',
      orgID: orgID,
      active: true,
      hashKey: 'test_hash_123'
    },
    {
      id: 2,
      username: 'testuser2',
      firstName: 'Anna',
      lastName: 'Schmidt',
      email: 'anna.schmidt@example.com',
      orgID: orgID,
      active: true,
      hashKey: 'test_hash_456'
    },
    {
      id: 3,
      username: 'testuser3',
      firstName: 'Peter',
      lastName: 'Müller',
      email: 'peter.mueller@example.com',
      orgID: orgID,
      active: true,
      hashKey: 'test_hash_789'
    }
  ];
};

// Aktive Benutzer einer Organisation abrufen
export const fetchActiveUsers = async (orgID: number): Promise<User[]> => {
  try {    
    const requestBody = { orgID };
    
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.USERS_ACTIVE), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      // Versuche die Fehlermeldung vom Server zu lesen
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.text();
        if (errorData) {
          try {
            const parsedError = JSON.parse(errorData);
            errorMessage = parsedError.error || parsedError.message || errorMessage;
          } catch {
            errorMessage = `${errorMessage} - ${errorData}`;
          }
        }
      } catch (e) {
        console.log('Could not read error response body');
      }
      
      // Wenn es ein SQL-Fehler ist, verwende Test-Daten
      if (typeof errorMessage === 'string' && (errorMessage.includes('SQL syntax') || errorMessage.includes('ER_PARSE_ERROR'))) {
        console.warn('SQL syntax error detected, using test data instead');
        const testUsers = getTestUsers(orgID);
        localStorage.setItem(`activeUsers_${orgID}`, JSON.stringify(testUsers));
        localStorage.setItem(`activeUsers_${orgID}_timestamp`, Date.now().toString());
        return testUsers;
      }
      
      throw new Error(`Failed to fetch active users: ${errorMessage}`);
    }

    const users: User[] = await response.json();

    
    // Prüfen ob hashKey-Felder vorhanden sind
    const usersWithHashKey = users.filter(user => user.hashKey);
    
    if (usersWithHashKey.length === 0) {
      console.warn('No users with hashKey found! Check API response structure.');
      console.warn('This might indicate that the API returns a different field name for the hash.');
    }
    
    // Benutzer im lokalen Storage speichern
    localStorage.setItem(`activeUsers_${orgID}`, JSON.stringify(users));
    localStorage.setItem(`activeUsers_${orgID}_timestamp`, Date.now().toString());
        
    return users;
  } catch (error: any) {
    console.error('Error fetching active users:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      orgID: orgID
    });
    
    // Bei Fehler: lokale Daten zurückgeben
    const cachedUsers = localStorage.getItem(`activeUsers_${orgID}`);
    const fallbackUsers = cachedUsers ? JSON.parse(cachedUsers) : [];
    return fallbackUsers;
  }
};

// Lokale Benutzerdaten abrufen
export const getLocalActiveUsers = (orgID: number): User[] => {
  const cachedUsers = localStorage.getItem(`activeUsers_${orgID}`);
  const users: User[] = cachedUsers ? JSON.parse(cachedUsers) : [];
  
  // Debug: Überprüfen der lokalen Daten
  if (users.length > 0) {
    const usersWithHashKey = users.filter((user: User) => user.hashKey);
  }
  
  return users;
};

// Benutzer anhand Hash finden
export const findUserByHash = (hash: string, orgID: number): User | null => {
  const users = getLocalActiveUsers(orgID);
  
  // Direkte Suche nach dem hashKey (1:1 Vergleich)
  return users.find(user => user.hashKey === hash) || null;
};

// Text 1:1 aus NFC-Record lesen (exakt wie auf dem Chip gespeichert)
const extractTextFromRecord = (record: { data?: DataView }): string => {
  const data = record.data;
  if (!data || data.byteLength === 0) return '';

  return new TextDecoder('utf-8', { fatal: false })
    .decode(data)
    .replace(/\0/g, '')
    .trim();
};

// Fallback für standardmässig formatierte NDEF-Text-Records (z.B. NFC Tools)
const extractNdefTextRecord = (record: { recordType?: string; data?: DataView }): string => {
  const data = record.data;
  if (!data || data.byteLength <= 1 || record.recordType !== 'text') return '';

  const statusByte = data.getUint8(0);
  const languageCodeLength = statusByte & 0x3f;
  const textStart = 1 + languageCodeLength;
  if (languageCodeLength > 10 || textStart >= data.byteLength) return '';

  const encoding = (statusByte & 0x80) ? 'utf-16' : 'utf-8';
  return new TextDecoder(encoding, { fatal: false })
    .decode(data.buffer.slice(data.byteOffset + textStart, data.byteOffset + data.byteLength))
    .replace(/\0/g, '')
    .trim();
};

const extractHashFromNdefMessage = (records: Array<{ recordType?: string; data?: DataView }>): string => {
  for (const record of records) {
    const text = extractTextFromRecord(record);
    if (text) return text;
  }
  return '';
};

const findUserByNfcRecords = (records: Array<{ recordType?: string; data?: DataView }>, orgID: number): User | null => {
  for (const record of records) {
    const rawText = extractTextFromRecord(record);
    if (rawText) {
      const user = findUserByHash(rawText, orgID);
      if (user) return user;
    }

    const ndefText = extractNdefTextRecord(record);
    if (ndefText && ndefText !== rawText) {
      const user = findUserByHash(ndefText, orgID);
      if (user) return user;
    }
  }
  return null;
};

// NFC-Scan durchführen
export const scanNFC = async (): Promise<NFCScanResult> => {
  // Prüfen ob NFC verfügbar ist
  if (!('NDEFReader' in window)) {
    return {
      success: false,
      error: 'NFC wird von diesem Browser nicht unterstützt'
    };
  }

  try {
    const ndef = new (window as any).NDEFReader();
    
    return new Promise((resolve) => {
      let hasResolved = false;
      
      const resolveOnce = (result: NFCScanResult) => {
        if (!hasResolved) {
          hasResolved = true;
          resolve(result);
        }
      };
      
      ndef.scan()
        .then(() => {
          
          ndef.addEventListener('reading', (event: any) => {
            try {
              // Prüfen ob Records vorhanden sind
              if (!event.message || !event.message.records || event.message.records.length === 0) {
                resolveOnce({
                  success: false,
                  error: 'NFC-Token ist leer oder hat kein gültiges Format'
                });
                return;
              }

              const hash = extractHashFromNdefMessage(event.message.records);
              
              // Prüfen ob Hash nicht leer ist
              if (!hash) {
                resolveOnce({
                  success: false,
                  error: 'NFC-Token enthält keinen Hash'
                });
                return;
              }
              
              // OrgID aus Token extrahieren
              const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
              let orgID = 0;
              
              if (accessToken) {
                try {
                  const payload = JSON.parse(atob(accessToken.split('.')[1]));
                  orgID = payload.orgID;
                } catch {}
              }
                            
              if (!orgID) {
                resolveOnce({
                  success: false,
                  error: 'Keine Organisation gefunden'
                });
                return;
              }
              
              // Benutzer anhand des 1:1 gelesenen Texts finden
              const user = findUserByNfcRecords(event.message.records, orgID);
              
              if (user) {
                resolveOnce({
                  success: true,
                  user
                });
              } else {
                // Debug: Alle verfügbaren Benutzer anzeigen
                const allUsers = getLocalActiveUsers(orgID);
                
                resolveOnce({
                  success: false,
                  error: `Benutzer nicht gefunden (Hash: ${hash})`
                });
              }
            } catch (error: any) {
              console.error('Error processing NFC data:', error);
              resolveOnce({
                success: false,
                error: `Fehler beim Lesen des NFC-Tokens: ${error.message}`
              });
            }
          });
          
          ndef.addEventListener('readingerror', (error: any) => {
            console.error('NFC reading error:', error);
            resolveOnce({
              success: false,
              error: 'NFC-Scan fehlgeschlagen'
            });
          });
        })
        .catch((error: any) => {
          console.error('NFC scan error:', error);
          resolveOnce({
            success: false,
            error: `NFC-Fehler: ${error.message}`
          });
        });
        
      // Timeout nach 30 Sekunden
      setTimeout(() => {
        if (!hasResolved) {
          resolveOnce({
            success: false,
            error: 'NFC-Scan Timeout - Kein Tag erkannt'
          });
        }
      }, 30000);
    });
  } catch (error: any) {
    console.error('NFC initialization error:', error);
    return {
      success: false,
      error: `NFC-Fehler: ${error.message}`
    };
  }
};

// Benutzerdaten synchronisieren
export const syncUsers = async (): Promise<User[]> => {
  // Check if we're online before making API calls
  if (!navigator.onLine) {
    console.log('📡 App is offline, using cached users for syncUsers');
    const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    let orgID = 0;
    
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        orgID = payload.orgID;
      } catch {}
    }
    
    if (!orgID) {
      throw new Error('Keine Organisation gefunden');
    }
    
    // Return cached users instead of making API call
    return getLocalActiveUsers(orgID);
  }
  
  const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
  let orgID = 0;
  
  if (accessToken) {
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      orgID = payload.orgID;
    } catch {}
  }
  
  if (!orgID) {
    throw new Error('Keine Organisation gefunden');
  }
  
  return await fetchActiveUsers(orgID);
};

// Prüfen ob NFC verfügbar ist
export const isNFCAvailable = (): boolean => {
  return 'NDEFReader' in window;
};

// Kontinuierliches NFC-Scannen
export const startContinuousNFCScan = (onUserFound: (user: User) => void, onError?: (error: string) => void): (() => void) => {
  // Prüfen ob NFC verfügbar ist
  if (!('NDEFReader' in window)) {
    onError?.('NFC wird von diesem Browser nicht unterstützt');
    return () => {}; // Leere Cleanup-Funktion
  }

  let isScanning = false;
  let ndef: any = null;
  let isStopped = false;
  let lastScannedHash = '';
  let lastScanTime = 0;
  const SCAN_COOLDOWN = 2000; // 2 Sekunden Verzögerung zwischen Scans

  const processNFCTag = async (event: any) => {
    if (isStopped) return;
    
    try {
      
      // Prüfen ob Records vorhanden sind
      if (!event.message || !event.message.records || event.message.records.length === 0) {
        onError?.('NFC-Token ist leer oder hat kein gültiges Format');
        return;
      }

      const hash = extractHashFromNdefMessage(event.message.records);
      
      // Prüfen ob Hash nicht leer ist
      if (!hash) {
        onError?.('NFC-Token enthält keinen Hash');
        return;
      }
      
      // Prüfen ob derselbe Hash kürzlich gescannt wurde
      const now = Date.now();
      if (hash === lastScannedHash && (now - lastScanTime) < SCAN_COOLDOWN) {
        return;
      }
      
      // Hash und Zeit aktualisieren
      lastScannedHash = hash;
      lastScanTime = now;
      
      // OrgID aus Token extrahieren
      const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      let orgID = 0;
      
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          orgID = payload.orgID;
        } catch {}
      }
      
      
      if (!orgID) {
        onError?.('Keine Organisation gefunden');
        return;
      }
      
      // Benutzer anhand des 1:1 gelesenen Texts finden
      const user = findUserByNfcRecords(event.message.records, orgID);
      
      if (user) {
        onUserFound(user);
      } else {
        // Debug: Alle verfügbaren Benutzer anzeigen
        const allUsers = getLocalActiveUsers(orgID);
        
        onError?.(`Benutzer nicht gefunden (Hash: ${hash})`);
      }
    } catch (error: any) {
      console.error('Error processing NFC data:', error);
      onError?.(`Fehler beim Lesen des NFC-Tokens: ${error.message}`);
    }
  };

  const startScan = async () => {
    if (isStopped || isScanning) return;
    
    try {
      isScanning = true;
      ndef = new (window as any).NDEFReader();
      
      await ndef.scan();
      
      ndef.addEventListener('reading', processNFCTag);
      
      ndef.addEventListener('readingerror', (error: any) => {
        console.error('NFC reading error:', error);
        onError?.('NFC-Scan fehlgeschlagen');
      });
      
    } catch (error: any) {
      console.error('NFC scan error:', error);
      onError?.(`NFC-Fehler: ${error.message}`);
      isScanning = false;
      
      // Nach Fehler erneut versuchen
      setTimeout(() => {
        if (!isStopped) {
          startScan();
        }
      }, 2000);
    }
  };

  // Scan starten
  startScan();

  // Cleanup-Funktion zurückgeben
  return () => {
    isStopped = true;
    isScanning = false;
    if (ndef) {
      try {
        ndef.removeEventListener('reading', processNFCTag);
      } catch (e) {
        console.log('Error removing NFC event listener:', e);
      }
    }
  };
}; 